import { FC, useEffect, useRef, useState } from "react";
import { useAuthStore } from "../../hooks/useAuthStore";
import { useNavigate } from "react-router-dom";
import { useSearcherStore } from "../../hooks/useSearcherStore";
import { useIntakeCartStore } from "../../hooks/useIntakeCartStore";
import { ProductSearch } from "../../types/slices/productType";
import { DetailIntakeCart, IntakeCart as IntakeCartInterface } from "../../types/slices/intakeCartType";
import { useSupplierStore } from "../../hooks/useSupplierStore";
import { moneyCL } from "../../toolbox/helpers/money-helpers";
import { Autocomplete, Button, CircularProgress, Divider, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CustomTable from "../../components/common/CustomTable/CustomTable";
import { ApiStatus } from "../../types/api/status";
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import DeleteIcon from '@mui/icons-material/Delete';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import { useProductEntryStore } from "../../hooks/useProductEntryStore";

interface listProduct {
    page?: number
    perPage?: number
    idcustomer?: number
    exactCode?: string
    sellingProducts?: boolean
    mostSearchedProducts?: boolean
}

export const IntakeCart: FC = () => {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const { status: statusSupplier, supppliers, getSuppliers } = useSupplierStore()
    const { status: statusSearcher, getSearchProducts } = useSearcherStore()
    const { status: statusIntakeCart, intakeCarts, getIntakeKart, createIntakeCart, deleteProductCart,
        updateQuantity, clearIntakeCart } = useIntakeCartStore()
    const { status: statusProductEntry, createProductEntry } = useProductEntryStore()

    const autocompleteRef = useRef<HTMLInputElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const searchRef = useRef<HTMLInputElement>(null)

    const [products, setProducts] = useState<ProductSearch[]>([])
    const [selectedIntakeCart, setSelectedIntakeCart] = useState<IntakeCartInterface | null>(null)
    const [detailIntakeCart, setDetailIntakeCart] = useState<DetailIntakeCart[]>([])

    const [search, setSearch] = useState<string>('')
    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)
    const [totalRows, setTotalRows] = useState<number>(0)

    const [showBestSellingProducts, setShowBestSellingProducts] = useState<boolean>(false)
    const [mostSearchedProducts, setMostSearchedProducts] = useState<boolean>(false)
    const [disableConfirm, setDisabledConfirm] = useState<boolean>(false)

    useEffect(() => {
        // getSuppliersApi()
        getIntakeCartAPI()
    }, [])

    useEffect(() => {
        if (intakeCarts.length > 0) {
            onFetchDataIntakeCart(intakeCarts[0])
        } else {
            onFetchDataIntakeCart(null)
        }
    }, [intakeCarts])

    useEffect(() => {
        if (search === '') {
            getProductsApi()
        } else {
            const timer = setTimeout(() => getProductsApi(), 500)
            return () => clearTimeout(timer)
        }
    }, [page, perPage, search, showBestSellingProducts, mostSearchedProducts])

    const getSuppliersApi = async () => {
        await getSuppliers()
    }

    const getProductsApi = async () => {
        const payload: listProduct = {
            page: page,
            perPage: perPage
        }
        if (showBestSellingProducts === true) payload.sellingProducts = true
        if (mostSearchedProducts === true) payload.mostSearchedProducts = true
        // if (selectedCustomer) payload.idcustomer = selectedCustomer.id
        if (search !== '') {
            payload.exactCode = search
            payload.sellingProducts = undefined
            payload.mostSearchedProducts = undefined
        }
        const resp: any = await getSearchProducts(payload)
        setProducts(resp.detail || [])
        setTotalRows(resp.total || 0)
    }

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Shift') {
                searchRef.current?.focus()
                setSearch('')
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    const getIntakeCartAPI = async () => {
        await getIntakeKart({
            iduser_created: user?.id,
            status_entry: 0,
            // idcustomer: selectedCustomer?.id || undefined
        })
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const onFetchDataIntakeCart = (data) => {
        setSelectedIntakeCart(data || null)
        setDetailIntakeCart(data?.details || [])
    }

    const handleSelectProduct = async (idproduct: number, quantity?: number) => {
        let payload = {
            idproduct: idproduct,
            quantity: quantity ?? 1
        }

        const response = await createIntakeCart(payload)
        if (response?.id) {
            onFetchDataIntakeCart(response)
            if (inputRef.current) {
                const input = inputRef.current.querySelector('input')
                if (input) {
                    input.focus()
                    input.select()
                }
            }
        }
    }

    const handleDeleteItem = async (iddetail: number) => {
        const response = await deleteProductCart(iddetail)
        if (response?.id) {
            onFetchDataIntakeCart(response)
        } else {
            onFetchDataIntakeCart(null)
        }
    }

    const handleChangeQuantity = async (iddetail: number, value: string) => {
        const newQuantity = value

        setDetailIntakeCart((prev) => {
            return prev.map((detail) => {
                if (detail.id === iddetail) {
                    return { ...detail, quantity: newQuantity }
                }
                return detail
            })
        })
    }

    const onBlurQuantity = async (iddetail: number, value: string) => {
        const response = await updateQuantity(iddetail, { quantity: value })
        if (response?.id) {
            onFetchDataIntakeCart(response)
            if (disableConfirm) setDisabledConfirm(false)
        }
    }

    const onAddIntakeCart = async (rowSelected) => {
        await handleSelectProduct(rowSelected?.idproduct)
    }

    const onDeleteIntakeCart = (rowSelected) => {
        handleDeleteItem(rowSelected?.id)
    }

    const handleSearch = async (event) => {
        if (event.key === 'Enter') {
            if (products.length === 1) {
                setSearch('')
                await onAddIntakeCart(products[0])
                if (inputRef.current) {
                    const input = inputRef.current.querySelector('input')
                    if (input) input.focus()
                }
            }
        }
    }

    const onConfirm = () => {
        navigate('/product-entry/preview/' + selectedIntakeCart?.id)
    }

    const columns = [
        { type: 'text', field: 'codesystem_product', label: 'Código', align: 'center' },
        { type: 'text', field: 'name_product', label: 'Nombre', align: 'center' },
        { type: 'text', field: 'unitmeasure_product.abbreviation', label: 'Unid. Med.', align: 'center', format: (row) => row?.unitmeasure_product?.abbreviation },
        { type: 'text', field: 'cost', label: 'Costo', align: 'right', format: (row) => moneyCL(row.cost_product) },
        { type: 'detail', field: 'detail', label: 'Detalle', align: 'center' },
    ]

    return (
        <Grid container spacing={2}>
            {/* Buscador y Productos */}
            <Grid item container xs={12} sm={12} md={8} lg={8} xl={7} spacing={2}>

                {/** LISTA DE PRODUCTOS */}
                <Grid item xs={12}>
                    <div
                        style={{
                            border: '1px solid #E0E0E0',
                            borderRadius: '8px',
                            padding: '15px',
                            backgroundColor: '#F9F9F9',
                            minHeight: '500px'
                        }}
                    >
                        <TextField
                            inputRef={searchRef}
                            variant="outlined"
                            placeholder="Buscar producto..."
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#516E80' }} />
                                    </InputAdornment>
                                ),
                                style: {
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                },
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#E0E0E0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#516E80',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#516E80',
                                    },
                                },
                            }}
                        />
                        <CustomTable
                            columns={columns}
                            loading={statusSearcher === ApiStatus.FETCHING}
                            rows={products || []}
                            onRowClick={() => { }}
                            hasOptions
                            dense
                            onHelper={onAddIntakeCart}
                            tooltip_helper={"Agregar al carrito"}
                            icon_helper={<AddShoppingCartIcon fontSize="small" />}

                            totalRows={totalRows}
                            rowsPerPage={perPage}
                            page={page}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            withCustomPagination={true}
                        />
                    </div>
                </Grid>
            </Grid >
            {/* Carrito y Totales */}
            <Grid item container xs={12} sm={12} md={4} lg={4} xl={5} spacing={2} >
                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold' }}>
                        Artículos de la cesta ({detailIntakeCart.length})
                    </Typography>
                    <Divider />
                </Grid>
                <Grid item xs={12} sx={{ height: '60%' }}>
                    <Grid container spacing={2}
                        sx={{
                            height: '100%',
                            overflowY: 'auto',
                            paddingRight: '8px',
                            maxHeight: '475px'
                        }}
                    >
                        {detailIntakeCart.length > 0 ? (
                            detailIntakeCart.map((element, index) => (
                                <Grid item xs={12} key={element.id}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            border: '1px solid #E0E0E0',
                                            borderRadius: '8px',
                                            padding: '15px',
                                            backgroundColor: '#F9F9F9',
                                            position: 'relative',
                                            gap: '15px',
                                        }}
                                    >
                                        {/* Detalles del producto */}
                                        <div style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 'bold', color: '#333', marginBottom: '5px' }}
                                            >
                                                {element.product}
                                            </Typography>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 'bold', color: '#516E80' }}
                                                >
                                                    {moneyCL(element?.cost || 0)}
                                                </Typography>
                                            </div>
                                        </div>
                                        {/* Cantidad */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            <Typography variant="body2" sx={{ color: '#555' }}>
                                                Cantidad:
                                            </Typography>
                                            <TextField
                                                ref={inputRef}
                                                id={"quantity_" + element.idproduct}
                                                type="text"
                                                name={"quantity_" + element.idproduct}
                                                size="small"
                                                value={element?.quantity}
                                                onChange={(e) => {
                                                    const { value } = e.target

                                                    const isValid =
                                                        element?.idunitmeasure_product === 1
                                                            ? /^\d+$/.test(value)
                                                            : /^\d*\.?\d{0,2}$/.test(value)

                                                    if (isValid || value === "") {
                                                        handleChangeQuantity(element?.id, value)
                                                    }
                                                }}
                                                onFocus={() => setDisabledConfirm(true)}
                                                onBlur={() => {
                                                    onBlurQuantity(element?.id, element?.quantity)
                                                }}
                                                inputProps={{
                                                    min: 1,
                                                    inputMode: "decimal",
                                                    pattern: "[0-9]*"
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <span>{element?.unitmeasure_abbreviation || ''}</span>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    width: '80px',
                                                    "& .MuiInputBase-root": { fontSize: '14px' },
                                                }}
                                            />
                                        </div>
                                        {/* Botón Eliminar */}
                                        <IconButton
                                            sx={{
                                                backgroundColor: '#FF6666',
                                                color: '#FFF',
                                                "&:hover": { backgroundColor: '#E05555' },
                                            }}
                                            onClick={() => onDeleteIntakeCart(element)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                </Grid>
                            ))
                        ) : (
                            <Grid item xs={12}>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        padding: '20px',
                                        textAlign: 'center',
                                        backgroundColor: '#F9F9F9',
                                        height: '100%'
                                    }}
                                >
                                    <ProductionQuantityLimitsIcon
                                        sx={{ fontSize: '50px', color: '#9C9C9C', marginBottom: '10px' }}
                                    />
                                    <Typography sx={{ color: '#3f3f3f', fontSize: '16px' }}>
                                        Aún no tienes productos en tu canasta
                                    </Typography>
                                </div>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Button
                        sx={{
                            borderRadius: '20px',
                            fontSize: '12px',
                            backgroundColor: '#91CBF9',
                            color: '#fff',
                            width: '100%',
                            padding: '6px 12px',
                            "&:hover": {
                                backgroundColor: "#73B0E6",
                            },
                        }}
                        disabled={statusIntakeCart === ApiStatus.FETCHING || disableConfirm}
                        onClick={onConfirm}
                    >
                        {(statusIntakeCart === ApiStatus.FETCHING) && <CircularProgress color="inherit" size={20} />}
                        Confirmar
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    )
}